<template>
  <div>
    <div class="installments-table-card">
      <table class="installments-table">
        <thead>
          <tr>
            <th style="width: 8%">Parcela</th>
            <th style="width: 15%">Status</th>
            <th style="width: 20%">Valor</th>
            <th style="width: 20%">Valor Pago</th>
            <th style="width: 15%">Data de Pagamento</th>
            <th style="width: 15%">Data da Baixa</th>
            <th style="width: 7%"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="installment in policyInstallments" :key="installment.id">
            <td>
              {{ installment.installmentNumber || '-' }} de
              {{ installmentsNumber }}
            </td>
            <td>
              <vs-chip
                v-if="installment.status"
                class="status-chip"
                :color="getInstallmentStatusColor(installment.status)"
              >
                {{ TRANSLATED_POLICY_INSTALLMENT_STATUS[installment.status] }}
              </vs-chip>
              <span v-else>-</span>
            </td>
            <td class="value-cell">
              <span
                :class="{
                  'old-value':
                    installment.status ===
                    POLICY_INSTALLMENT_STATUS_ENUM.CANCELED,
                }"
              >
                {{ installment.installmentValue | currency }}
              </span>
              <span
                v-if="
                  installment.originalInstallmentValue &&
                  installment.originalInstallmentValue !==
                    installment.installmentValue
                "
                class="old-value"
              >
                {{ installment.originalInstallmentValue | currency }}
              </span>
            </td>
            <td>
              <span v-if="installment.paymentValue">
                {{ installment.paymentValue | currency }}
              </span>
              <span v-else> - </span>
            </td>
            <td>
              {{
                installment.paymentDate
                  ? formatDateWithMoment(installment.paymentDate)
                  : '-'
              }}
            </td>
            <td>
              {{
                installment.clearanceDate
                  ? formatDateWithMoment(installment.clearanceDate)
                  : '-'
              }}
            </td>
            <td class="history-action">
              <vs-button
                color="primary"
                type="filled"
                radius
                icon-pack="feather"
                icon="icon-list"
                v-tooltip="'Histórico'"
                @click="openInstallmentHistory(installment)"
              >
              </vs-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <ModalPolicyInstallmentHistory
      :active-prop="isHistoryModalActive"
      :history-prop="selectedInstallmentHistory"
      :installment-number="selectedInstallmentNumber"
      @close="closeInstallmentHistory"
    />
  </div>
</template>

<script>
import {
  POLICY_INSTALLMENT_STATUS_ENUM,
  TRANSLATED_POLICY_INSTALLMENT_STATUS,
} from '@/utils/constants';
import moment from 'moment/moment';
import ModalPolicyInstallmentHistory from './ModalPolicyInstallmentHistory.vue';
import historyApi from '@/services/history.api';
import functions from '@/utils/functions';
import notify from '@/utils/notify';

export default {
  name: 'PolicyInstallment',
  components: {
    ModalPolicyInstallmentHistory,
  },
  data() {
    return {
      isHistoryModalActive: false,
      selectedInstallmentHistory: [],
      selectedInstallmentNumber: '-',
      MONETARY_FIELDS: [
        'installment_value',
        'iof',
        'net_prize',
        'interest',
        'payment_value',
        'original_installment_value',
      ],
      DATE_FIELDS: ['payment_date', 'credit_date'],
      DATETIME_FIELDS: ['clearance_date'],
    };
  },
  computed: {
    TRANSLATED_POLICY_INSTALLMENT_STATUS() {
      return TRANSLATED_POLICY_INSTALLMENT_STATUS;
    },
    POLICY_INSTALLMENT_STATUS_ENUM() {
      return POLICY_INSTALLMENT_STATUS_ENUM;
    },
  },
  props: {
    installmentsNumber: {
      type: Number,
      required: true,
    },
    policyInstallments: {
      type: Array,
      required: true,
    },
  },
  methods: {
    getInstallmentStatusColor(data) {
      const warning = [this.POLICY_INSTALLMENT_STATUS_ENUM.PENDING];
      const success = [
        this.POLICY_INSTALLMENT_STATUS_ENUM.PAID,
        this.POLICY_INSTALLMENT_STATUS_ENUM.UNDERPAID,
      ];
      const danger = [undefined, this.POLICY_INSTALLMENT_STATUS_ENUM.CANCELED];

      if (warning.includes(data)) return 'warning';
      if (success.includes(data)) return 'success';
      if (danger.includes(data)) return 'danger';
    },
    formatDatetimeWithMoment(date) {
      return moment(date).format('DD/MM/YYYY HH:mm:ss');
    },
    formatDateWithMoment(date) {
      return moment(date).format('DD/MM/YYYY');
    },
    formatDatetimeForTable(date) {
      return moment(date).format('DD/MM/YYYY HH:mm');
    },
    formatMonetaryValue(value) {
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) return value;
      return functions.formataReal(numberValue);
    },
    formatDateValue(value) {
      if (!value || value === '-') return value;
      const date = moment(value);
      if (!date.isValid()) return value;
      return date.format('DD/MM/YYYY');
    },
    formatDatetimeValue(value) {
      if (!value || value === '-') return value;
      const date = moment(value);
      if (!date.isValid()) return value;
      return date.format('DD/MM/YYYY HH:mm:ss');
    },
    normalizeHistoryItem(item) {
      const fieldName = item.fieldName;
      let oldValue = item.oldValue ? item.oldValue : '-';
      let newValue = item.newValue ? item.newValue : '-';

      if (this.MONETARY_FIELDS.includes(fieldName)) {
        oldValue = this.formatMonetaryValue(oldValue);
        newValue = this.formatMonetaryValue(newValue);
      }

      if (this.DATE_FIELDS.includes(fieldName)) {
        oldValue = this.formatDateValue(oldValue.replace(/"/g, ''));
        newValue = this.formatDateValue(newValue.replace(/"/g, ''));
      }
      if (this.DATETIME_FIELDS.includes(fieldName)) {
        oldValue = this.formatDatetimeValue(oldValue.replace(/"/g, ''));
        newValue = this.formatDatetimeValue(newValue.replace(/"/g, ''));
      }

      return {
        ...item,
        fieldName,
        oldValue,
        newValue,
        user: item.user || { name: item.userName || 'System' },
      };
    },
    async getInstallmentHistory(installmentId) {
      this.$vs.loading();
      try {
        const res = await historyApi.getPolicyInstallmentsHistory(
          installmentId,
        );
        const rows = Array.isArray(res) ? res : [];
        return rows.map((item) => this.normalizeHistoryItem(item));
      } catch (error) {
        notify.notifyFail(this.$vs, `Erro ao buscar os logs: ${error}`);
        return [];
      } finally {
        this.$vs.loading.close();
      }
    },
    async openInstallmentHistory(installment) {
      this.selectedInstallmentNumber = String(
        installment.installmentNumber || '-',
      );
      this.selectedInstallmentHistory = [];
      this.isHistoryModalActive = true;
      this.selectedInstallmentHistory = await this.getInstallmentHistory(
        installment.id,
      );
    },
    closeInstallmentHistory() {
      this.isHistoryModalActive = false;
      this.selectedInstallmentHistory = [];
      this.selectedInstallmentNumber = '-';
    },
  },
};
</script>
<style scoped lang="scss">
.installments-table-card {
  border: 1px solid #b8bcc5;
  border-radius: 9px;
  overflow: hidden;
  background: #fff;
}

.installments-table-header {
  padding: 12px 16px;
  border-bottom: 1px solid #edf0f4;

  h4 {
    margin: 0;
    font-size: 14px;
    line-height: 18px;
    color: #2e3447;
  }

  span {
    display: inline-block;
    margin-top: 4px;
    font-size: 12px;
    line-height: 16px;
    color: #7d8597;
  }
}

.installments-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  thead th {
    padding: 10px 16px;
    background: #fff;
    border-bottom: 1px solid #b8bcc5;
    text-align: left;
    font-family: 'Montserrat', 'Segoe UI', sans-serif;
    font-size: 12px;
    line-height: 16px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #1f232d;
    font-weight: 500;
  }

  tbody td {
    padding: 9px 16px;
    border-bottom: 1px solid #d6d9df;
    font-family: 'Montserrat', 'Segoe UI', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #3e424d;
    vertical-align: middle;
  }

  tbody tr:nth-child(even) td {
    background: #f1f2f4;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
}

.status-chip {
  margin: 0;
  font-size: 12px;
}

.value-cell {
  font-weight: 400;

  .old-value {
    margin-left: 8px;
    color: #9aa1ad;
    text-decoration: line-through;
    font-weight: 500;
    font-size: 12px;
  }
}

.history-action {
  width: 56px;
  text-align: right;
}

.history-action ::v-deep .vs-button {
  min-width: 30px;
  height: 30px;
  padding: 0;
  border-radius: 999px;
  box-shadow: none;
}

.history-action ::v-deep .vs-button:hover {
  opacity: 0.9;
}
</style>
